"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const react_1 = require("react");
const debounce_1 = tslib_1.__importDefault(require("lodash/debounce"));
const getBreakpoints_1 = tslib_1.__importDefault(require("../kds/getBreakpoints"));
/* Determine if the window is defined for SSR */
const isClient = () => typeof window !== 'undefined';
const DEFAULT_DIMENSIONS = {
    width: 1024,
    height: 1335
};
/* gets the current dimensions of the window if it exists */
const getWindowDimensions = () => {
    const dimensions = isClient()
        ? {
            width: window.innerWidth,
            height: window.innerHeight
        }
        : DEFAULT_DIMENSIONS;
    return dimensions;
};
/**
 * Determines the device type represent by the width pased in.
 * @param {number} width
 * @returns Object
 * {isMobile: boolean, isTablet: boolean, isDesktop:boolean}
 */
function getDeviceType(width, breakpoints) {
    const { sm: { upper: smBp }, md: { upper: mdBp } } = (0, getBreakpoints_1.default)(breakpoints);
    const device = { isMobile: false, isTablet: false, isDesktop: false };
    if (width < smBp) {
        return Object.assign(Object.assign({}, device), { isMobile: true });
    }
    if (width >= smBp && width < mdBp) {
        return Object.assign(Object.assign({}, device), { isTablet: true });
    }
    if (width >= mdBp) {
        return Object.assign(Object.assign({}, device), { isDesktop: true });
    }
    return device;
}
/**
 * SUMMARY:
 * This hook determines the current window dimensions and the type of device
 * the dimensions reflect.  It is responsive and will update the window dimensions when
 * the window is resized.
 *
 * !! WARNING !! - This hook should not be used for responsive UI styling becuase it has issues with server-side rendering.
 * Use the KDS <Responsive> component with useCssMediaQueries prop instead.  This hook can be used for logic
 * not related to UI styling on the client-side when the window is defined.
 *
 * @returns Object
 * Example:
 * {
 *   width: Number
 *   height: Number
 *   device: {
 *      isMobile: boolean
 *      isTablet: boolean
 *      isDesktop: boolean
 *    }
 * }
 */
function useWindowDimensions(breakpoints) {
    const [windowDimensions, setWindowDimensions] = (0, react_1.useState)(getWindowDimensions());
    const [deviceType, setDeviceType] = (0, react_1.useState)({
        isDesktop: true,
        isMobile: false,
        isTablet: false
    });
    const handleResize = () => {
        const dimensions = getWindowDimensions();
        setWindowDimensions(dimensions);
        setDeviceType(getDeviceType(dimensions.width, breakpoints));
    };
    (0, react_1.useEffect)(() => {
        const debouncedHandleResize = (0, debounce_1.default)(() => {
            handleResize();
        }, 50);
        window.addEventListener('resize', debouncedHandleResize);
        return () => {
            window.removeEventListener('resize', debouncedHandleResize);
        };
    }, []);
    (0, react_1.useEffect)(() => {
        const dimensions = getWindowDimensions();
        const deviceType = getDeviceType(dimensions.width, breakpoints);
        setWindowDimensions(dimensions);
        setDeviceType(deviceType);
    }, []);
    return Object.assign(Object.assign({}, windowDimensions), { device: deviceType });
}
exports.default = useWindowDimensions;
//# sourceMappingURL=useWindowDimensions.js.map